import React, { useCallback, useEffect } from 'react'
import { useSequencerStore, FX_IDS, type FxId } from '@/stores/sequencerStore'
import { useAudioEngine } from '@/hooks/useAudioEngine'
import { FxPerformButton, type FxMode } from './FxPerformButton'

/**
 * Six master-bus FX laid out as "Koala-style" perform buttons — each
 * button is the fader. Vertical drag inside a button sets that FX's
 * amount; pointer-down engages the FX and pointer-up disengages
 * (unless LATCH mode is on, which turns every FX button into a
 * toggle). The `filter` fader is unipolar low-pass — bottom = bypass,
 * top = fully muffled at 500 Hz. `tapeStop` is a binary serial effect;
 * its button doesn't react to Y drag, it just toggles.
 *
 * `latchMode` is local / transient — deliberately not persisted because
 * nobody wants their sequencer to come back after a restart with
 * latched reverb blasting.
 */
const FX_LABELS: Record<FxId, string> = {
  filter: 'Filter',
  delay: 'Delay',
  reverb: 'Reverb',
  bitcrush: 'Bitcrush',
  tapeStop: 'Tape Stop',
  stutter: 'Stutter',
}

const FX_HINTS: Record<FxId, string> = {
  filter: 'Low-pass cut · drag up for more muffle, bottom = bypass',
  delay: 'Ping-pong delay send',
  reverb: 'Plate-style wash',
  bitcrush: '4-bit quantize',
  tapeStop: 'Tape-halt master ramp · binary',
  stutter: 'Holds the last ~80 ms',
}

const FX_MODES: Record<FxId, FxMode> = {
  filter: 'unipolar',
  delay: 'unipolar',
  reverb: 'unipolar',
  bitcrush: 'unipolar',
  tapeStop: 'binary',
  stutter: 'unipolar',
}

export type FxBankVariant = 'bar' | 'rail'

interface FxBankProps {
  /**
   * `'bar'` — legacy horizontal 3/6-col layout for the old
   * bottom-of-page location. `'rail'` — vertical 2-col layout for
   * the right-side thumb-reachable placement on iPad. Defaults to
   * `'bar'` for back-compat.
   */
  variant?: FxBankVariant
}

export const FxBank: React.FC<FxBankProps> = ({ variant = 'bar' }) => {
  const fxState = useSequencerStore(s => s.fxState)
  const fxLevels = useSequencerStore(s => s.fxLevels)
  const setFxActive = useSequencerStore(s => s.setFxActive)
  const setFxLevel = useSequencerStore(s => s.setFxLevel)
  // Latch + Hold both live in the store — the sibling pills
  // rendered above the FX rail in SequencerPage drive them and
  // the rail card forwards their state into each perform pad.
  const latchMode = useSequencerStore(s => s.fxLatchMode)
  const setLatchMode = useSequencerStore(s => s.setFxLatchMode)
  const holdMode = useSequencerStore(s => s.fxHoldMode)
  const perfFilterMode = useSequencerStore(s => s.perfFilterMode)
  const setPerfFilterModeStore = useSequencerStore(s => s.setPerfFilterMode)
  const {
    setFx,
    setPerfFilterMode: setPerfFilterModeEngine,
    resume,
  } = useAudioEngine()

  // Previous versions seeded the bipolar filter at amount 0.5 so
  // "center tick" was the natural neutral. Now the filter is
  // unipolar (top = full muffle, bottom = bypass) — tap-on-engage
  // reads the touch Y directly, so no seeding is needed. Legacy
  // projects that persisted amount = 0.5 simply start mid-muffle,
  // which is fine as a unipolar "moderate" position.

  // Mirror the store's engaged set + amounts into the audio engine.
  // Both inputs feed engage() so dragging the fader while engaged
  // updates the wet gain in realtime.
  useEffect(() => {
    for (const id of FX_IDS) {
      setFx(id, fxState[id], fxLevels[id] ?? 1)
    }
  }, [fxState, fxLevels, setFx])

  // Push the store's filter-mode preference into the engine. Fires on
  // mount (so a persisted choice survives reload) and on every store
  // change thereafter (so clicking the HP/LP toggle propagates live).
  useEffect(() => {
    setPerfFilterModeEngine(perfFilterMode)
  }, [perfFilterMode, setPerfFilterModeEngine])

  const toggleFilterMode = useCallback(() => {
    setPerfFilterModeStore(perfFilterMode === 1 ? 2 : 1)
  }, [perfFilterMode, setPerfFilterModeStore])

  const engage = useCallback(
    (fxId: FxId, active: boolean) => {
      setFxActive(fxId, active)
    },
    [setFxActive],
  )

  const isRail = variant === 'rail'

  return (
    <div
      className={
        isRail
          ? 'rounded-xl bg-surface-sunken border border-brd-subtle p-2 h-full flex flex-col gap-2'
          : 'rounded-xl bg-surface-sunken border border-brd-subtle p-3'
      }
    >
      {/* Non-rail variant gets its descriptor at the top. The rail
          variant has no header of its own — SequencerPage renders a
          Latch pill as a sibling above the rail card so its top
          border aligns with the Song / Step / Pads card borders. */}
      {!isRail && (
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
            FX Bank · drag vertically to set amount · LATCH to keep engaged
          </span>
        </div>
      )}
      <div
        className={
          isRail
            ? // Single column — each FX gets the full rail width
              // (~220–260px) and ~80px of height. At this size, labels
              // like "TAPE STOP" / "BITCRUSH" fit on one line and the
              // touch target is comfortable on iPad. The earlier 2-col
              // layout squeezed each button to ~115px wide at the
              // iPad/Catalyst breakpoint, truncating labels.
              'grid grid-cols-1 grid-rows-6 auto-rows-fr gap-2 flex-1'
            : 'grid grid-cols-3 sm:grid-cols-6 gap-2'
        }
      >
        {FX_IDS.map(id => {
          const mode = FX_MODES[id]
          // Bipolar FX want a default amount of 0.5 (neutral bypass)
          // on first render; unipolar/binary default to the existing
          // full-intensity value so behaviour matches the prior
          // slider-at-100% shipping default.
          const fallback = mode === 'bipolar' ? 0.5 : 1
          // Filter gets a per-mode label + hint so the user knows
          // immediately what the fader does today. The wrapping
          // <div> carries the inline HP/LP toggle in a top-right
          // corner overlay — small target, out of the drag path.
          const isFilter = id === 'filter'
          const filterModeLabel = perfFilterMode === 1 ? 'HP' : 'LP'
          const label = isFilter
            ? `${FX_LABELS[id]} · ${filterModeLabel}`
            : FX_LABELS[id]
          const hint = isFilter
            ? perfFilterMode === 1
              ? 'High-pass cut · drag up to cut bass, bottom = bypass'
              : 'Low-pass cut · drag up for more muffle, bottom = bypass'
            : FX_HINTS[id]
          const button = (
            <FxPerformButton
              key={id}
              fxId={id}
              label={label}
              hint={hint}
              mode={mode}
              active={fxState[id]}
              amount={fxLevels[id] ?? fallback}
              latchMode={latchMode}
              holdMode={holdMode}
              onEngage={engage}
              onAmountChange={setFxLevel}
              onResume={resume}
            />
          )
          if (!isFilter) return button
          return (
            <div key={id} className="relative">
              {button}
              <button
                type="button"
                onPointerDown={e => {
                  // Swallow — the FxPerformButton wraps a pointer-down
                  // listener that would otherwise engage the filter.
                  // Using onPointerDown instead of onClick lets the
                  // toggle fire before any engage() starts.
                  e.stopPropagation()
                }}
                onClick={e => {
                  e.stopPropagation()
                  toggleFilterMode()
                }}
                title={`Filter mode — tap to switch to ${perfFilterMode === 1 ? 'low-pass' : 'high-pass'}`}
                aria-label={`Filter mode, currently ${perfFilterMode === 1 ? 'high-pass' : 'low-pass'}. Tap to switch.`}
                className="absolute top-1 right-1 z-10 rounded border border-brd-subtle bg-surface-raised/80 px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase tracking-wider text-content-secondary backdrop-blur-sm transition-colors hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
              >
                {filterModeLabel}
              </button>
            </div>
          )
        })}
      </div>
      {!isRail && (
        <button
          type="button"
          aria-pressed={latchMode}
          onClick={() => setLatchMode(!latchMode)}
          title={
            latchMode
              ? 'LATCH on — taps toggle FX engage state. Tap LATCH to return to momentary.'
              : 'LATCH off — FX engage while pressed. Tap LATCH to keep them on.'
          }
          className={`rounded-md border text-[10px] font-mono uppercase tracking-[0.16em] py-1.5 transition-colors mt-3 w-full ${
            latchMode
              ? 'bg-second text-[color:var(--second-ink,white)] border-second'
              : 'bg-surface-raised/40 text-content-secondary border-brd-subtle hover:border-brd-emphasis hover:text-content-primary'
          }`}
        >
          {latchMode ? 'LATCH · on' : 'LATCH'}
        </button>
      )}
    </div>
  )
}
